/* eslint-disable */
import React from 'react';
import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  TableRow,
  TableCell,
  Badge,
  ESGScoreDisplay,
  Button,
  FundamentalScoreDisplay,
  Tooltip,
} from './index';
import type { Hotel } from '../../hooks/useHotels';
import {
  HotelInfoContainer,
  HotelNameContainer,
  HotelName,
  HotelLocation,
  HotelBrandInfo,
  NoScoreText,
  NoDataText,
} from '../HotelsTable/HotelsTable.styled';
import { useAuth } from '../../contexts/AuthContext';
import { useBoostModal, useImageWithFallback } from '../../hooks';

interface HotelTableRowProps {
  hotel: Hotel;
  onActionClick: (hotel: Hotel) => void;
  currentStatusFilter: string;
}

const getActionText = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'pending':
    case 'rejected':
      return 'View';
    case 'published':
    case 'scored':
    default:
      return 'Go';
  }
};

// Helper function to calculate fundamental score ratio
const getFundamentalScoreRatio = (
  fundamentalScore: Hotel['fundamental_score']
) => {
  if (!fundamentalScore) {
    return '-';
  }

  const scores = Object.values(fundamentalScore);
  const nonZeroCount = scores.filter((score: number) => score > 0).length;
  const totalCount = scores.length;

  return `${nonZeroCount}/${totalCount}`;
};

// Status mapping for badges - updated for new API
const getStatusBadge = (status: string, isActionRequired: boolean = false) => {
  // If action is required, show red badge
  if (isActionRequired) {
    return <Badge variant="error">{status}</Badge>;
  }

  switch (status?.toLowerCase()) {
    case 'published':
      return <Badge variant="success">Published</Badge>;
    case 'pending':
      return <Badge variant="warning">Pending</Badge>;
    case 'draft':
      return <Badge variant="error">Draft</Badge>;
    case 'submitted':
      return <Badge variant="info">Submitted</Badge>;
    default:
      return <Badge variant="default">{status}</Badge>;
  }
};

export const getHotelTierBadge = (status: string) => {
  switch (status) {
    case 'kickstart':
      return <Badge variant="warning">Kickstart</Badge>;
    case 'essentials':
      return <Badge variant="warning">Essentials</Badge>;
    case 'boost':
      return <Badge variant="success">Boost</Badge>;
    case 'unlock':
      return <Badge variant="error">Unlock</Badge>;
    case 'active':
      return <Badge variant="success">Active</Badge>;
    case 'inactive':
      return <Badge variant="error">Inactive</Badge>;
    default:
      return <Badge variant="default">{status.toLowerCase()}</Badge>;
  }
};

// Hotel status mapping
const getHotelStatusBadge = (
  status: string,
  hotelId: string,
  navigate: ReturnType<typeof useNavigate>
) => {
  if (!status) {
    return '-';
  }

  const getTooltipContent = (statusLower: string) => {
    if (statusLower === 'essentials') {
      return 'Upgrade to boost';
    } else if (statusLower === 'boost' || statusLower === 'add benefits') {
      return '';
    } else {
      return 'Add Benefits';
    }
  };

  const statusLower = status?.toLowerCase();
  const tooltipContent = getTooltipContent(statusLower);

  const handleClick = () => {
    if (statusLower !== 'boost') {
      navigate(`/upgrade?hotelId=${hotelId}`);
    }
  };

  const badge = getHotelTierBadge(statusLower);

  const wrappedBadge =
    statusLower !== 'boost' ? (
      <div
        onClick={handleClick}
        style={{ cursor: 'pointer', display: 'inline-block' }}
      >
        {badge}
      </div>
    ) : (
      badge
    );

  // Only wrap with tooltip if there's content to show
  if (tooltipContent) {
    return (
      <div style={{ display: 'inline-block' }}>
        <Tooltip
          content={tooltipContent}
          position="top"
          size='sm'
          icon={wrappedBadge}
          iconStyle={{ margin: 0 }}
          maxWidth={200}
        />
      </div>
    );
  }

  return wrappedBadge;
};

const HotelTableRow: React.FC<HotelTableRowProps> = ({
  hotel,
  onActionClick,
  currentStatusFilter,
}) => {
  // console.log("Hotel data in HotelTableRow component :: ", hotel);
  const navigate = useNavigate();
 
  const { isSuperAdmin } = useAuth();
  const { imageUrl, hasError, isLoading } = useImageWithFallback(
    hotel.hotel_image,
    { fallbackUrl: '/defaultHotel.jpg' }
  );
  const getActionToolTipContent = (statusLower: string) => {
    if (isSuperAdmin) {
      return 'View Hotel';
    } else {
      switch (statusLower) {
        case 'complete assessment':
        case 'in progress':
          return 'Complete Your Hotel Submission';
        default:
          return 'View Your Submission';
      }
    }
  };

  return (
    <TableRow>
      {/* Hotel Info */}
      <TableCell align="left">
        <div className="flex gap-2 items-center">
          {/* image container */}
          <div
            className={`w-full h-16 max-w-16 rounded-md overflow-hidden flex-shrink-0 relative`}
            style={{
              filter: isLoading || hasError ? 'grayscale(100%)' : 'none',
              WebkitFilter:
                'grayscale(100%)' /* Ensure webkit prefix is used */,

              /* Workarounds for Safari rendering bug */
              WebkitTransform: 'translateZ(0)',
              transform:
                'translateZ(0)' /* This often forces a proper render */,
            }}
          >
            <img
              src={imageUrl}
              alt="hotel-image"
              className={`object-cover w-full h-full `}
            />
          </div>
          <HotelInfoContainer>
            <HotelNameContainer>
              <HotelName>{hotel.hotel_location_name}</HotelName>
            </HotelNameContainer>
            <HotelLocation>
              {hotel.hotel_location_city}, {hotel.hotel_location_country}
            </HotelLocation>
            <HotelBrandInfo>
              {hotel.brand} • {hotel.chain}
            </HotelBrandInfo>
          </HotelInfoContainer>
        </div>
      </TableCell>

      {/* Hotel Status */}
      <TableCell
        align="center"
        className={`!capitalize ${
          isSuperAdmin && '!cursor-default !pointer-events-none'
        }`}
      >
        {getHotelStatusBadge(hotel.alo_hotel_status, hotel.id, navigate)}
      </TableCell>

      {/* Score */}
      <TableCell align="center">
        {hotel.score_alo > 0 ||
        hotel.score_e > 0 ||
        hotel.score_s > 0 ||
        hotel.score_g > 0 ? (
          <ESGScoreDisplay
            scoreE={hotel.score_e}
            scoreS={hotel.score_s}
            scoreG={hotel.score_g}
            scoreAlo={hotel.score_alo}
            className="w-12 mx-auto"
          />
        ) : (
          <NoScoreText>-</NoScoreText>
        )}
      </TableCell>

      {/* Fundamental Score */}
      {/* <TableCell align="center">
        {hotel.fundamental_score ? (
          <FundamentalScoreDisplay score={getFundamentalScoreRatio(hotel.fundamental_score)} />
        ) : (
          <NoScoreText>-</NoScoreText>
        )}
      </TableCell> */}

      {/* Submission Status */}
      <TableCell align="center" className="uppercase">
        {getStatusBadge(
          hotel.alo_submission_status,
          currentStatusFilter === 'ACTION_REQUIRED'
        )}
      </TableCell>

      {/* Resubmission */}
      {/* <TableCell align="center">
        {hotel.alo_resubmission_status ? (
          <Badge variant="info">{hotel.alo_resubmission_status}</Badge>
        ) : (
          <NoDataText>N/A</NoDataText>
        )}
      </TableCell> */}

      {/* Action */}
      <TableCell align="center" className="text-sm">
        <p className="mb-1 text-[var(--color-gray-800)] font-medium cursor-pointer">
          {getActionText(hotel.alo_submission_status)}
        </p>
        <Tooltip
          content={getActionToolTipContent(
            hotel.alo_submission_status?.toLowerCase() || ''
          )}
          position="top"
          icon={
            <Button
              variant="outline"
              size="sm"
              className="!rounded-full p-1 text-gray-400 text-center hover:bg-gray-50"
              onClick={() => onActionClick(hotel)}
            >
              <ChevronRight size={20} />
            </Button>
          }
        />
      </TableCell>
    </TableRow>
  );
};

export default HotelTableRow;
